import { useEffect, useRef, useState } from "react";

/**
 * TARİH ARALIĞI SEÇİCİ.
 *
 * Ön ayar listesi Shopify'ın rapor filtresiyle BİREBİR aynı sırada ve aynı
 * anlamda tutulur — kullanıcı iki paneli yan yana açıp rakam karşılaştırıyor;
 * "Son 7 gün" iki yerde farklı gün sayarsa hangisinin doğru olduğu tartışılır.
 *
 * ★ SEÇİLEN ARALIK HER ZAMAN TARİH OLARAK YAZILIR ("05.08.2026 – 11.08.2026").
 * "Son 7 gün"ün bugünü içerip içermediği gibi sorular böylece ekranda cevaplanır,
 * kullanıcının tahmin etmesine gerek kalmaz.
 */

export type RangePreset =
  | "today" | "yesterday" | "today_yesterday"
  | "last_7" | "last_14" | "last_28" | "last_30"
  | "this_week" | "last_week" | "this_month" | "last_month"
  | "max" | "custom";

export const RANGE_ORDER: RangePreset[] = [
  "today", "yesterday", "today_yesterday",
  "last_7", "last_14", "last_28", "last_30",
  "this_week", "last_week", "this_month", "last_month",
  "max", "custom",
];

export const RANGE_LABELS: Record<RangePreset, string> = {
  today: "Bugün",
  yesterday: "Dün",
  today_yesterday: "Bugün ve dün",
  last_7: "Son 7 gün",
  last_14: "Son 14 gün",
  last_28: "Son 28 gün",
  last_30: "Son 30 gün",
  this_week: "Bu hafta",
  last_week: "Geçen hafta",
  this_month: "Bu ay",
  last_month: "Geçen ay",
  max: "Maksimum",
  custom: "Özel",
};

export interface RangeValue {
  preset: RangePreset;
  /** "yyyy-aa-gg" — yalnızca `custom` için. */
  from?: string;
  /** "yyyy-aa-gg" — DAHİL. */
  to?: string;
}

interface Props {
  value: RangeValue;
  onChange: (v: RangeValue) => void;
  /** Sunucunun çözdüğü aralık ("05.08.2026 – 11.08.2026"). */
  resolvedLabel?: string | null;
  busy?: boolean;
}

/** Bugünün "yyyy-aa-gg" biçimi — tarih girişlerinin üst sınırı. */
function todayIso(): string {
  const d = new Date();
  const z = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

export function DateRangePicker({ value, onChange, resolvedLabel, busy }: Props) {
  const [open, setOpen] = useState(false);
  /**
   * Panel sağa mı hizalansın?
   *
   * ★ SABİT HİZALAMA İKİ SAYFADAN BİRİNİ BOZAR. Panel `left-0` ile açılıyordu;
   * Finans ve Panel ekranlarında seçici sağ üstte durduğu için 26 rem'lik
   * kutunun sağ yarısı EKRANIN DIŞINDA kalıyor, "Uygula" düğmesi görünmüyordu.
   * `right-0` yapmak ise İptal Edilenler ekranını bozardı: orada seçici solda.
   *
   * Bu yüzden hizalama ölçülerek belirlenir: panel sağdan taşacaksa sağa,
   * taşmayacaksa sola yaslanır. Yeni bir sayfaya eklendiğinde de doğru çalışır.
   */
  const [sagaYasli, setSagaYasli] = useState(false);
  /**
   * ★ "ÖZEL" PANELİ YEREL DURUMLA AÇILIR, ÜST BİLEŞENİN DEĞERİYLE DEĞİL.
   *
   * Tarih kutuları eskiden `value.preset === "custom"` koşuluna bağlıydı ve o
   * değeri ancak `pick()` içindeki `onChange` değiştiriyordu. O çağrı (tarihler
   * boşken sunucuya geçersiz istek gönderdiği için) kaldırılınca kutular HİÇ
   * ÇİZİLMEZ OLDU: "Özel"e basılıyor, hiçbir şey açılmıyordu.
   *
   * Doğru ayrım şu: "kullanıcı özel aralık GİRİYOR" bir ARAYÜZ durumudur ve
   * burada yaşamalıdır; "seçili aralık özeldir" ise VERİ durumudur ve ancak iki
   * tarih de dolduğunda üst bileşene bildirilir (`applyCustom`).
   */
  const [ozelAcik, setOzelAcik] = useState(value.preset === "custom");
  const [draftFrom, setDraftFrom] = useState(value.from ?? "");
  const [draftTo, setDraftTo] = useState(value.to ?? "");
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Açılırken ve pencere yeniden boyutlanırken hizalamayı ölç.
  useEffect(() => {
    if (!open) return;
    const olc = () => {
      const r = boxRef.current?.getBoundingClientRect();
      if (!r) return;
      // Panel genişliği (26rem) + kenar payı. Solda da yer yoksa sola yaslanır:
      // taşmayı sağa kaydırmak, ekranın soluna taşırmaktan iyidir.
      const PANEL = 416;
      const sagdaYerYok = r.left + PANEL > window.innerWidth - 16;
      const soldaYerVar = r.right - PANEL > 16;
      setSagaYasli(sagdaYerYok && soldaYerVar);
    };
    olc();
    window.addEventListener("resize", olc);
    return () => window.removeEventListener("resize", olc);
  }, [open]);

  // Dışarı tıklama ve Esc ile kapanır — modal değil, hafif bir açılır liste.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (p: RangePreset) => {
    setError(null);
    if (p !== "custom") {
      setOzelAcik(false);
      onChange({ preset: p });
      setOpen(false);
      return;
    }
    setOzelAcik(true);
    /**
     * "Özel" seçilince liste açık kalır; kullanıcı tarihleri girip uygular.
     *
     * ★ BURADA `onChange` ÇAĞRILMAZ. Eskiden çağrılıyordu ve tarihler henüz boş
     * olduğu için `{preset:"custom"}` yayılıyordu. İki sonucu vardı:
     *   · Sunucu bunu reddediyor ("Özel aralık için başlangıç ve bitiş tarihi
     *     gerekli") → her "Özel" tıklamasında boşa giden hatalı bir istek.
     *   · Çağıran sayfa değeri localStorage'a yazıyordu → sonraki oturumda ekran
     *     BOŞ açılıyordu. (Finance.tsx'e ayrıca iki katman güvenlik ağı eklendi.)
     * Aralık yalnızca `applyCustom` içinde, iki tarih de doluyken yayılır.
     */
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) {
      setError("Başlangıç ve bitiş tarihini seçin.");
      return;
    }
    if (draftFrom > draftTo) {
      setError("Başlangıç tarihi bitişten sonra olamaz.");
      return;
    }
    setError(null);
    onChange({ preset: "custom", from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const buttonText =
    value.preset === "custom" && resolvedLabel
      ? resolvedLabel
      : RANGE_LABELS[value.preset];

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="btn-secondary inline-flex items-center gap-2"
      >
        <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
        </svg>
        <span>{buttonText}</span>
        {busy ? (
          <span className="text-xs text-gray-500">hesaplanıyor…</span>
        ) : (
          <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
          </svg>
        )}
      </button>

      {open ? (
        <div
          role="listbox"
          aria-label="Tarih aralığı"
          className={`absolute z-30 mt-2 w-[26rem] max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-2 shadow-lg ${
            sagaYasli ? "right-0" : "left-0"
          }`}
        >
          {/*
            ★ KAYDIRMA YOK, İKİ SÜTUN VAR.
            Liste `max-h-80 overflow-y-auto` ile tek sütundu; 13 ön ayardan
            son dördü ("Bu ay", "Geçen ay", "Maksimum", "Özel") görünmüyordu ve
            kullanıcı onların HİÇ OLMADIĞINI sandı. Görünmeyen seçenek, olmayan
            seçenektir. İki sütunda hepsi tek bakışta görünür.
          */}
          <ul className="grid grid-cols-2 gap-x-1">
            {RANGE_ORDER.map((p) => {
              // "Özel" için seçili görünüm YEREL duruma bakar: kullanıcı ona
              // bastığı anda işaretlenmeli, tarihleri girmesini beklemeden.
              const selected = p === "custom" ? ozelAcik : value.preset === p && !ozelAcik;
              return (
                <li key={p} className={p === "custom" ? "col-span-2 mt-1 border-t border-gray-200 pt-1" : ""}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => pick(p)}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                      selected ? "font-medium text-primary-700" : "text-gray-800"
                    }`}
                  >
                    {/* Radyo görünümü: seçili olan hem renk hem DOLU daire ile
                        belirtilir — yalnızca renge dayalı işaret bırakılmaz. */}
                    <span
                      aria-hidden="true"
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                        selected ? "border-primary-600" : "border-gray-400"
                      }`}
                    >
                      {selected ? <span className="h-2 w-2 rounded-full bg-primary-600" /> : null}
                    </span>
                    {RANGE_LABELS[p]}
                  </button>
                </li>
              );
            })}
          </ul>

          {ozelAcik ? (
            <div className="mt-2 border-t border-gray-200 pt-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="tarih-bas" className="label">Başlangıç</label>
                  <input
                    id="tarih-bas"
                    type="date"
                    max={todayIso()}
                    value={draftFrom}
                    onChange={(e) => setDraftFrom(e.target.value)}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label htmlFor="tarih-bit" className="label">Bitiş</label>
                  <input
                    id="tarih-bit"
                    type="date"
                    max={todayIso()}
                    value={draftTo}
                    onChange={(e) => setDraftTo(e.target.value)}
                    className="input w-full"
                  />
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-600">Bitiş tarihi dâhildir.</p>
              {error ? (
                <p role="alert" className="mt-1 text-xs text-red-700">{error}</p>
              ) : null}
              <button type="button" onClick={applyCustom} className="btn-primary mt-2 w-full">
                Uygula
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {resolvedLabel ? (
        <p className="mt-1 text-xs text-gray-600">{resolvedLabel}</p>
      ) : null}
    </div>
  );
}

export default DateRangePicker;
